/**
 * Orders.jsx - Purchase orders list page.
 *
 * Displays a filterable, sortable DataTable of purchase orders from
 * Business Central. Supports search, status filtering, and an
 * order detail modal with header info and line items.
 *
 * Route: /orders
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { Search } from "lucide-react";
import { DataTable, Badge, Modal, Loading, useToast } from "../components";
import {
  getPurchaseOrders,
  getPurchaseOrderWithLines,
} from "../api/orders";
import { useCompany } from "../company/CompanyContext";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const STATUS_OPTIONS = [
  "",
  "Open",
  "Partially Received",
  "Received",
  "Closed",
  "Cancelled",
];

/* ------------------------------------------------------------------ */
/* Number formatters                                                   */
/* ------------------------------------------------------------------ */

const CURRENCY = new Intl.NumberFormat("en-PH", {
  style: "currency",
  currency: "PHP",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const NUMBER = new Intl.NumberFormat("en-US");

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function OrdersPage() {
  const { toast } = useToast();
  const { selectedCompany } = useCompany();

  /* Data state */
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* Filter state */
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  /* Modal state */
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  /* ---------------------------------------------------------------- */
  /* Data fetching                                                     */
  /* ---------------------------------------------------------------- */

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Map company IDs to exact names used by backend API
      const companyMap = {
        "ysu": "YSU Marketing Corporation",
        "cody-marketing": "Cody Marketing Corporation", 
        "jmu-cebu": "JMU Marketing Inc.- Cebu",
        "jmu": "JMU Marketing Inc.",
      };
      
      const companyFilter = selectedCompany?.id !== "all" 
        ? companyMap[selectedCompany?.id] || selectedCompany?.name 
        : undefined;
        
      console.log("[Orders] Sending company filter:", companyFilter);
      
      const data = await getPurchaseOrders({ pageSize: 1000, company: companyFilter });
      /* DB API returns { total, rows } */
      setOrders(data.rows || data || []);
    } catch (err) {
      setError(err.message || "Failed to load purchase orders");
      toast("Failed to load purchase orders", "bad");
    } finally {
      setLoading(false);
    }
  }, [toast, selectedCompany]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  /* ---------------------------------------------------------------- */
  /* Derived data                                                      */
  /* ---------------------------------------------------------------- */

  const filteredOrders = useMemo(() => {
    let result = orders;

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (order) =>
          (order.number || "").toLowerCase().includes(q) ||
          (order.vendorNumber || "")
            .toLowerCase()
            .includes(q) ||
          (order.vendorName || "")
            .toLowerCase()
            .includes(q) ||
          (order.purchaserCode || "")
            .toLowerCase()
            .includes(q)
      );
    }

    if (statusFilter) {
      result = result.filter((order) => {
        const orderStatus = order.status || "";
        return orderStatus === statusFilter;
      });
    }

    return result;
  }, [orders, search, statusFilter]);

  /* ---------------------------------------------------------------- */
  /* Order detail modal                                                */
  /* ---------------------------------------------------------------- */

  const handleRowClick = useCallback(
    async (row) => {
      const orderNo = row.number;
      if (!orderNo) return;

      setSelectedOrder(row);
      setModalLoading(true);

      try {
        const fullOrder = await getPurchaseOrderWithLines(orderNo);
        setSelectedOrder(fullOrder);
      } catch (err) {
        toast(
          "Failed to load order details: " + (err.message || "Unknown error"),
          "bad"
        );
      } finally {
        setModalLoading(false);
      }
    },
    [toast]
  );

  const handleCloseModal = useCallback(() => {
    setSelectedOrder(null);
  }, []);

  /* ---------------------------------------------------------------- */
  /* Table columns                                                     */
  /* ---------------------------------------------------------------- */

  const columns = useMemo(
    () => [
      { key: "number", label: "PO" },
      {
        key: "vendor",
        label: "Vendor",
        render: (_, row) =>
          row.vendorName ||
          row.vendorNumber ||
          "\u2014",
      },
      {
        key: "orderDate",
        label: "Ordered date",
        type: "date",
      },
      {
        key: "expected",
        label: "Expected date",
        type: "date",
      },
      {
        key: "status",
        label: "Status",
        render: (_, row) => {
          const status = row.status || "";
          return <Badge text={status} />;
        },
      },
      {
        key: "lines",
        label: "Lines",
        render: (_, row) => {
          const val = row.lines ?? row.line_count ?? row.lineCount;
          return val != null ? NUMBER.format(Number(val)) : "\u2014";
        },
      },
      {
        key: "qtyOrdered",
        label: "Qty ordered",
        render: (_, row) =>
          row.qtyOrdered != null ? NUMBER.format(Number(row.qtyOrdered)) : "\u2014",
      },
      {
        key: "qtyReceived",
        label: "Qty received",
        render: (_, row) =>
          row.qtyReceived != null ? NUMBER.format(Number(row.qtyReceived)) : "\u2014",
      },
      {
        key: "buyer",
        label: "Buyer",
        render: (_, row) =>
          row.buyerName || row.purchaserCode || row.buyer || "\u2014",
      },
      {
        key: "location",
        label: "Location",
        render: (_, row) =>
          row.locationCode || "\u2014",
      },
      {
        key: "value",
        label: "Value",
        render: (_, row) => {
          const amount = Number(row.totalAmountIncludingTax) || 0;
          return CURRENCY.format(amount);
        },
      },
    ],
    []
  );

  /* ---------------------------------------------------------------- */
  /* Modal detail columns                                              */
  /* ---------------------------------------------------------------- */

  const modalLineColumns = useMemo(
    () => [
      {
        key: "sequence",
        label: "Line no.",
        render: (_, row) => row.sequence || row.lineNo || "",
      },
      {
        key: "item",
        label: "Item",
        render: (_, row) => row.itemNumber || row.description || "",
      },
      {
        key: "description",
        label: "Description",
        render: (_, row) => row.description || "",
      },
      {
        key: "quantity",
        label: "Qty ordered",
        render: (_, row) => NUMBER.format(Number(row.quantity) || 0),
      },
      {
        key: "qtyReceived",
        label: "Qty received",
        render: (_, row) => NUMBER.format(Number(row.quantityReceived) || 0),
      },
      {
        key: "qtyRejected",
        label: "Qty rejected",
        render: (_, row) => NUMBER.format(Number(row.quantityRejected) || 0),
      },
      {
        key: "uom",
        label: "UOM",
        render: (_, row) => row.uom || "\u2014",
      },
      {
        key: "unitCost",
        label: "Unit cost",
        render: (_, row) => CURRENCY.format(Number(row.unitCost) || 0),
      },
      {
        key: "amount",
        label: "Amount",
        render: (_, row) => CURRENCY.format(Number(row.amountExcludingTax) || 0),
      },
      {
        key: "promisedDate",
        label: "Promised date",
        render: (_, row) => formatDate(row.promisedDate),
      },
      {
        key: "receiptDate",
        label: "Receipt date",
        render: (_, row) => formatDate(row.receiptDate),
      },
      {
        key: "lineStatus",
        label: "Status",
        render: (_, row) => {
          const status = row.status || "";
          return status ? <Badge text={status} /> : "\u2014";
        },
      },
    ],
    []
  );

  /* ---------------------------------------------------------------- */
  /* Modal header details                                              */
  /* ---------------------------------------------------------------- */

  const orderHeaderDetails = useMemo(() => {
    if (!selectedOrder) return [];

    const vendorName = selectedOrder.vendorName || selectedOrder.vendorNumber || "\u2014";
    const vendorNo = selectedOrder.vendorNumber || "\u2014";
    const status = selectedOrder.status || "\u2014";
    const orderedDate = selectedOrder.orderDate || selectedOrder.documentDate || "\u2014";
    const expectedDate = selectedOrder.expectedDate || "\u2014";
    const buyer = selectedOrder.buyerName || selectedOrder.purchaserCode || selectedOrder.buyer || "\u2014";
    const location = selectedOrder.locationCode || "\u2014";
    const currency = selectedOrder.currencyCode || "PHP";
    const totalAmount = selectedOrder.totalAmountIncludingTax || 0;

    return [
      { label: "Vendor", value: `${vendorName} (${vendorNo})` },
      { label: "Status", value: status, isBadge: true },
      { label: "Ordered", value: orderedDate, isDate: true },
      { label: "Expected", value: expectedDate, isDate: true },
      { label: "Buyer", value: buyer },
      { label: "Location", value: location },
      { label: "Currency", value: currency },
      { label: "Value", value: CURRENCY.format(Number(totalAmount) || 0) },
    ];
  }, [selectedOrder]);

  /* ---------------------------------------------------------------- */
  /* Modal lines data                                                  */
  /* ---------------------------------------------------------------- */

  const orderLines = useMemo(() => {
    if (!selectedOrder) return [];
    const lines =
      selectedOrder.purchaseOrderLines ||
      selectedOrder.PurchaseLines?.value ||
      selectedOrder.PurchaseLines ||
      [];
    return lines;
  }, [selectedOrder]);

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */

  return (
    <>
      <Loading show={loading} text="Loading purchase orders..." />

      {/* Error banner */}
      {error && !loading && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ color: "var(--bad)" }}>
            {error}
            <button
              className="btn tiny"
              style={{ marginLeft: 12 }}
              onClick={loadOrders}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div style={styles.filterBar}>
        <div style={styles.searchWrap}>
          <Search
            size={14}
            style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-3)" }}
          />
          <input
            type="text"
            placeholder="Search orders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={styles.select}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.filter(Boolean).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {/* Count row */}
      <div style={styles.countRow}>
        <span style={styles.countBadge}>
          {filteredOrders.length} order
          {filteredOrders.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Data table */}
      <div className="card">
        <div className="card-body flush">
          <DataTable
            columns={columns}
            rows={filteredOrders}
            onRow={handleRowClick}
            emptyText="No purchase orders match your filters"
          />
        </div>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <Modal
          title={`Purchase Order ${selectedOrder.number || ""}`}
          onClose={handleCloseModal}
          width="wide"
        >
          {modalLoading ? (
            <div style={styles.modalLoading}>
              <div className="muted">Loading order details...</div>
            </div>
          ) : (
            <>
              {/* Header info grid */}
              <div style={styles.headerGrid}>
                {orderHeaderDetails.map((detail) => (
                  <div key={detail.label} style={styles.headerItem}>
                    <span style={styles.headerLabel}>{detail.label}</span>
                    {detail.isBadge ? (
                      <Badge text={detail.value} />
                    ) : detail.isDate ? (
                      <span style={styles.headerValue}>
                        {formatDate(detail.value)}
                      </span>
                    ) : (
                      <span style={styles.headerValue}>{detail.value}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Lines table */}
              <div style={{ marginTop: 18 }}>
                <h4
                  style={{
                    marginBottom: 10,
                    fontSize: 13,
                    fontWeight: 650,
                    color: "var(--ink-2)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  Order Lines ({orderLines.length})
                </h4>
                <div style={{ overflowX: "auto" }}>
                  <DataTable
                    columns={modalLineColumns}
                    rows={orderLines}
                    emptyText="No lines found for this order"
                  />
                </div>
              </div>
            </>
          )}
        </Modal>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function formatDate(value) {
  if (!value || value === "\u2014") return "\u2014";
  try {
    return new Date(value).toLocaleDateString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(value);
  }
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const styles = {
  filterBar: {
    display: "flex",
    gap: 14,
    marginBottom: 16,
    flexWrap: "wrap",
    alignItems: "center",
    padding: "14px 16px",
    background: "var(--panel)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius)",
  },
  searchWrap: {
    position: "relative",
    flex: "1 1 240px",
    minWidth: 200,
  },
  searchInput: {
    width: "100%",
    padding: "9px 12px 9px 34px",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    fontSize: 13,
    background: "var(--bg)",
    color: "var(--ink)",
    outline: "none",
  },
  select: {
    padding: "9px 12px",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    fontSize: 13,
    background: "var(--bg)",
    color: "var(--ink)",
    cursor: "pointer",
    minWidth: 170,
  },
  countRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  countBadge: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--ink-2)",
    padding: "6px 12px",
    background: "var(--surface-2)",
    borderRadius: "var(--radius-sm)",
  },
  modalLoading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  headerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "14px 18px",
  },
  headerItem: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  headerLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--ink-3)",
  },
  headerValue: {
    fontSize: 13,
    fontWeight: 550,
    color: "var(--ink)",
    wordBreak: "break-word",
  },
};
